import React from 'react';
import {
  Backdrop, Box, Button, Fade, IconButton, Modal, Typography, alpha, useTheme,
  CircularProgress, Alert,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import ContactFormModal from '../../components/Forms/ContactFormModal';
import ContactFormFields from '../../components/Forms/ContactFormFields';
import { modalStyle } from '../Customer/client.constants';
import OpportunityFormFields from './components/forms/OpportunityFormFields';
import ClientAutocompleteField from './components/forms/ClientAutocompleteField';
import OwnerAutocompleteField from './components/forms/OwnerAutocompleteField';
import { useOpportunityForm } from './hooks/useOpportunityForm';
import { useContactCreate } from './hooks/useContactCreate';

// กว้างขึ้นจากเดิม (720) เพื่อรองรับเลย์เอาต์ 2 คอลัมน์ของ OpportunityFormFields.js —
// เป้าหมายคือให้ฟอร์มพอดีจอโดยไม่ต้อง scroll ขึ้นลง (ดู comment ในไฟล์นั้น)
const modalBoxStyle = { ...modalStyle, maxWidth: 1160 };

// สร้าง/แก้ไข opportunity — เลือกลูกค้าจาก Contact (contactType: client) เดียวกับหน้า
// /customer/client เป๊ะ พร้อมปุ่ม "+" เปิด ContactFormModal สร้างลูกค้าใหม่ในหน้าเดียว
// ไม่ navigate ออกไปหน้าอื่นเหมือน CreateFormQuotation.js เดิม
export default function OpportunityFormModal({
  open, onClose, opportunity, onSaved,
}) {
  const theme = useTheme();

  const {
    isEdit, form, setField, selectedContact, setSelectedContact,
    contactInput, setContactInput, contactOptions,
    selectedOwner, setSelectedOwner, ownerInput, setOwnerInput, ownerOptions,
    selectedOrderedBy, setSelectedOrderedBy, orderedByInput, setOrderedByInput,
    appointments, newAppointmentDate, setNewAppointmentDate,
    newAppointmentTitle, setNewAppointmentTitle, addAppointment, removeAppointment,
    saving, error, handleSave,
  } = useOpportunityForm({ open, opportunity, onSaved, onClose });

  const contactCreate = useContactCreate({ onCreated: setSelectedContact });

  return (
    <>
      <Modal open={open} onClose={onClose} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 500 }}>
        <Fade in={open}>
          <Box sx={modalBoxStyle}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: 3,
                py: 2,
                background: 'linear-gradient(135deg, rgb(var(--color-primary-700)) 0%, rgb(var(--color-primary-500)) 55%, rgb(var(--color-primary-450)) 100%)',
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.16)',
                  }}
                >
                  <BusinessCenterOutlinedIcon sx={{ color: '#fff' }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
                    {isEdit ? 'แก้ไขโอกาสทางธุรกิจ' : 'เพิ่มโอกาสทางธุรกิจ'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                    {isEdit ? form.opportunityNumber : 'กรอกข้อมูลโครงการเพื่อเริ่มติดตามโอกาสทางธุรกิจใหม่'}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small" onClick={onClose}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', p: 3, bgcolor: (t) => (t.palette.mode === 'light' ? '#f8fafc' : 'background.default') }}>
              <OpportunityFormFields
                form={form}
                onFieldChange={setField}
                isEdit={isEdit}
                clientField={(
                  <ClientAutocompleteField
                    options={contactOptions}
                    value={selectedContact}
                    onChange={setSelectedContact}
                    inputValue={contactInput}
                    onInputChange={setContactInput}
                    onAddNew={contactCreate.openAddContact}
                  />
                )}
                ownerField={(
                  <OwnerAutocompleteField
                    options={ownerOptions}
                    value={selectedOwner}
                    onChange={setSelectedOwner}
                    inputValue={ownerInput}
                    onInputChange={setOwnerInput}
                  />
                )}
                orderedByField={(
                  <OwnerAutocompleteField
                    label="ผู้ประสานงาน"
                    options={ownerOptions}
                    value={selectedOrderedBy}
                    onChange={setSelectedOrderedBy}
                    inputValue={orderedByInput}
                    onInputChange={setOrderedByInput}
                  />
                )}
                appointments={appointments}
                newAppointmentDate={newAppointmentDate}
                onNewAppointmentDateChange={setNewAppointmentDate}
                newAppointmentTitle={newAppointmentTitle}
                onNewAppointmentTitleChange={setNewAppointmentTitle}
                onAddAppointment={addAppointment}
                onRemoveAppointment={removeAppointment}
              />
              {error && <Alert severity="error" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1.25,
                px: 3,
                py: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                flexShrink: 0,
                bgcolor: 'background.paper',
              }}
            >
              <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 2, px: 3 }}>ยกเลิก</Button>
              <Button
                variant="contained" onClick={handleSave} disabled={saving}
                sx={{ borderRadius: 2, px: 4, boxShadow: `0 8px 20px -6px ${alpha(theme.palette.primary.main, 0.5)}` }}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              >
                บันทึก
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <ContactFormModal
        open={contactCreate.addContactOpen}
        onClose={contactCreate.closeAddContact}
        title="เพิ่มลูกค้าใหม่"
        subtitle="กรอกข้อมูลลูกค้าองค์กรหรือบุคคลธรรมดา"
        onSubmit={contactCreate.handleAddContactSubmit}
        error={contactCreate.contactError}
      >
        <ContactFormFields control={contactCreate.contactControl} cascade={contactCreate.contactCascade} setZipSelections={contactCreate.setContactZipSelections} />
        {contactCreate.contactSaving && <CircularProgress size={20} sx={{ mt: 1 }} />}
      </ContactFormModal>
    </>
  );
}
